import React, { useState, useEffect } from 'react';
import useFeedback from '../hooks/useFeedback';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  IconButton,
  Typography,
  Alert,
  AlertTitle,
  Chip,
  Tooltip,
  Card,
  CardContent,
  Stack,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  InputAdornment,
  Switch,
  FormControlLabel,
  Divider,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Lock,
  LockOpen,
  Description,
  LockReset as ResetPasswordIcon,
  People as PeopleIcon,
  Email as EmailIcon,
  Search as SearchIcon,
  Visibility,
  VisibilityOff,
  InfoOutlined,
  ForwardToInbox,
} from '@mui/icons-material';
import {
  getPeople,
  getEmployees,
  createEmployee,
  updateEmployee,
  deleteEmployee,
  lockoutEmployee,
  exportToExcel,
  getDepartments,
  getDepartmentSettings,
  downloadW4PDF,
  updateUser,
  createUser,
  resetUserPassword,
  sendPasswordReset,
  getManagedEmployees,
  setManagedEmployees,
  getManagerPermissions,
  setManagerPermissions,
  unlockUser,
  getAllTenants,
  getRoles,
  getHolidaySchedules,
  getPermissionRegistry,
} from '../api';
import { usesLegacyManagerPermissions, legacyPermissionToggles } from '../utils/managerPermissionToggles';
import { useAuth } from '../context/AuthContext';
import { useFeatures } from '../context/FeaturesContext';
import { payrollTabVisible } from '../utils/featureFlags';
import { peopleMissingHireDate } from '../utils/missingHireDate';
import { useConfirmDialog } from './ConfirmDialog';
import PersonFormDialog from './dialogs/PersonFormDialog';
import { getTenantTimezone, todayInTimezone } from '../utils/formatTime';
import DeleteEmployeeDialog from './dialogs/DeleteEmployeeDialog';
import TempPasswordDialog from './dialogs/TempPasswordDialog';
import SendPayRecordsDialog from './dialogs/SendPayRecordsDialog';
import PasswordStrengthIndicator, { isPasswordValid, getPasswordErrors } from './PasswordStrengthIndicator';

const PAY_SCOPE_HIDDEN_TEXT = 'Pay rates are shown only for your direct reports';

const defaultFormData = {
  first_name: '',
  last_name: '',
  email: '',
  hourly_rate: 25.00,
  is_active: true,
  overtime_enabled: true,
  overtime_threshold: 40,
  overtime_multiplier: 1.5,
  overtime_exception: '',
  overtime_exception_reason: '',
  overtime_disabled_basis: '',
  work_state: '',
  default_department_id: '',
  holiday_schedule_id: '',
  hire_date: '',
  employee_type: 'hourly',
  salary_amount: 0,
  pay_frequency: 'biweekly',
  filing_status: 'single',
  federal_allowances: 0,
  state_code: '',
  state_allowances: 0,
  additional_federal_withholding: 0,
  additional_state_withholding: 0,
  ssn_last_four: '',
  date_of_birth: '',
  w4_multiple_jobs: false,
  w4_dependent_credits: 0,
  w4_other_income: 0,
  w4_deductions: 0,
  w4_extra_withholding: 0,
  w4_signed_date: '',
  w4_basis: '',
  w4_lock_in_letter_date: '',
  w4_received_date: '',
  w4_lock_in_release_date: '',
  local_eit_rate: '',
  local_lst_annual: '',
  local_tax_jurisdiction: '',
  pa_uc_employee_rate: '',
  resident_psd_code: '',
  work_psd_code: '',
  local_tax_collector: '',
  street_address: '',
  city: '',
  zip_code: '',
  pto_accrual_rate: 0,
  pto_accrual_frequency: 'per_pay_period',
  pto_max_balance: 0,
  pto_carryover_limit: 0,
  pto_balance: 0,
  // Login fields
  loginEnabled: true,
  username: '',
  user_role: 'user',
  user_id: null,
  user_is_active: true,
  addingLogin: false,
  password: '',
  tenant_id: '',
};

const permLabel = (registry, key) => registry?.[key]?.label || key;

// Why the Manage Employees dialog exists for this person (Gitea #83).
function directReportsCaption(user, registry) {
  const perms = user?.directReportPerms || [];
  if (perms.length === 0) return 'Shown for every Manager login.';
  return `Shown because this person's role includes: ${perms.map(k => permLabel(registry, k)).join(', ')}.`;
}

function directReportsTip(user, registry) {
  const perms = user?.directReportPerms || [];
  const what = perms.length > 0 ? perms.map(k => permLabel(registry, k)).join(', ') : 'their manager permissions';
  return `Direct reports are the employees this person supervises. Their ${what} apply only to the employees selected here. Assigning someone also lets this person see those employees' records and time-off requests. No one except a tenant admin can approve or deny their own time off.`;
}

// 'YYYY-MM-DD' -> locale date; noon avoids the UTC-midnight day shift.
const fmtDay = (s) => new Date(`${s}T12:00:00`).toLocaleDateString();

// Sentence for the final-period PTO share the server posts on Deactivate
// (plan 071). Statuses that need no explanation return ''.
const finalAccrualText = (a) => {
  if (!a) return '';
  const priorText = a.prior_periods && a.prior_periods.length > 0
    ? ` Also posted ${a.prior_total} h for ${a.prior_periods.length} earlier uncredited pay period${a.prior_periods.length === 1 ? '' : 's'}.`
    : '';
  return finalAccrualCore(a) + priorText;
};
const finalAccrualCore = (a) => {
  const span = a.period_start && a.period_end ? ` for the pay period ${fmtDay(a.period_start)} – ${fmtDay(a.period_end)}` : '';
  switch (a.status) {
    case 'credited':
      return ` Final PTO accrual: +${a.hours} h${span} (${a.days_employed} of ${a.days_in_period} days). PTO balance is now ${a.balance_after} h.`;
    case 'capped':
      return ` Final PTO accrual: 0 h${span} — the balance is already at its maximum.`;
    case 'zero_days':
      return ` Final PTO accrual: 0 h${span} — no days employed in the period.`;
    case 'already_credited':
      return ` Final PTO accrual: this pay period was already credited (balance ${a.balance_after} h).`;
    case 'credited_by_payroll':
      return ` Final PTO accrual: a payroll run already credited this pay period.`;
    case 'no_pay_period':
      return ' No final PTO accrual was posted: no pay period on the Pay Periods tab covers today.';
    case 'failed':
      return ' The final PTO accrual could not be posted — adjust Current PTO Balance (hrs) in Edit.';
    default:
      return ''; // not_eligible, already_inactive, ledger_missing
  }
};

export default function People() {
  const { user: currentUser, isAdmin, isManager, isProgramAdmin, hasPermission } = useAuth();
  // The W-4 PDF route requires can_run_payroll (plan 031); don't offer a button that 403s.
  const canDownloadW4 = isProgramAdmin || hasPermission('can_run_payroll');
  // POST /employees/:id/send-pay-records needs can_run_payroll (plan 076); the
  // button follows the Payroll tab rule (Payroll on, or off with payroll history).
  const { features } = useFeatures();
  const canSendPayRecords = (isProgramAdmin || hasPermission('can_run_payroll')) && !!payrollTabVisible('Payroll', features);
  // PUT /employees/:id and PATCH /:id/overtime apply overtime changes only from
  // Manage employees / Run payroll holders (server canEditOvertime); hasPermission
  // is true for program admins and built-in admin/payroll admin.
  const canEditOvertime = hasPermission('can_manage_employees') || hasPermission('can_run_payroll');
  // PUT /auth/users/:id/managed-employees needs requireAdmin (can_manage_users or program admin).
  const canAssignReports = isProgramAdmin || hasPermission('can_manage_users');
  // The server says who can have direct reports (GET /employees/people,
  // can_have_direct_reports). Admins already see and decide everything, so the
  // button is not offered for them.
  const showDirectReports = (person) => canAssignReports && !!person.user_id
    && person.can_have_direct_reports === true && !['admin', 'program_admin'].includes(person.user_role);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { showConfirm, ConfirmDialogComponent } = useConfirmDialog();

  const canManage = isAdmin || isManager || isProgramAdmin;
  // PUT /employees/:id gives a built-in payroll admin the Tax / W-4 and Address
  // section only (server payroll_admin branch); everything else is read-only for them.
  const taxOnlyEditor = !isAdmin && !isProgramAdmin && currentUser?.role === 'payroll_admin';
  const canEditTax = isAdmin || isProgramAdmin || taxOnlyEditor;
  // PUT /auth/users/:id and POST /auth/users need requireAdmin (Manage users / program admin).
  const canEditLogin = isProgramAdmin || hasPermission('can_manage_users');

  // Hire Date is saved by PUT /employees/:id only for built-in admins and program admins
  // (the payroll admin and manager branches ignore it), so the banner is shown to exactly them.
  const canEditHire = isProgramAdmin || currentUser?.role === 'admin';

  const [people, setPeople] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentPerson, setCurrentPerson] = useState(null);
  const [formData, setFormData] = useState({ ...defaultFormData });
  const [dialogError, setDialogError] = useState('');
  const [error, setError] = useState('');
  const [success, showSuccess] = useFeedback(8000);

  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState({ open: false, employee: null, exported: false });
  const [tempPasswordDialog, setTempPasswordDialog] = useState({ open: false, password: '', employeeName: '' });
  const [payRecordsDialog, setPayRecordsDialog] = useState({ open: false, person: null });

  // Managed employees dialog
  const [managedDialog, setManagedDialog] = useState({ open: false, user: null });
  const [managedEmployeeIds, setManagedEmployeeIds] = useState([]);
  const [allEmployees, setAllEmployees] = useState([]);
  const [managerPerms, setManagerPerms] = useState(null);
  const [permRegistry, setPermRegistry] = useState({});

  // Reset password dialog
  const [resetDialog, setResetDialog] = useState({ open: false, user: null });
  const [resetPassword, setResetPassword] = useState('');
  const [showResetPassword, setShowResetPassword] = useState(false);

  const [departments, setDepartments] = useState([]);
  const [departmentsEnabled, setDepartmentsEnabled] = useState(false);
  const [holidaySchedules, setHolidaySchedules] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [roles, setRoles] = useState([]);

  useEffect(() => {
    loadPeople();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadPeople = async () => {
    try {
      const res = await getPeople();
      setPeople(res.data);
      // Load departments
      try {
        const settingsRes = await getDepartmentSettings();
        const enabled = !!settingsRes.data.departments_enabled;
        setDepartmentsEnabled(enabled);
        if (enabled) {
          const deptsRes = await getDepartments();
          setDepartments(deptsRes.data);
        }
      } catch (e) { /* departments may not exist */ }
      // Load holiday schedules for the assignment dropdown
      try {
        const schedRes = await getHolidaySchedules();
        setHolidaySchedules((schedRes.data || []).filter(s => s.is_active));
      } catch (e) { /* holiday schedules may not exist */ }
      // Load tenants for program admin
      if (isProgramAdmin) {
        try {
          const tRes = await getAllTenants();
          setTenants(tRes.data);
        } catch (e) { /* ignore */ }
      }
      // Load roles for the role dropdown
      try {
        const rolesRes = await getRoles();
        setRoles(rolesRes.data);
      } catch (e) { /* ignore - fall back to built-in roles */ }
    } catch (err) {
      setError('Failed to load people');
      console.error(err);
    }
  };

  const filteredPeople = people.filter(p => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    const name = `${p.first_name} ${p.last_name}`.toLowerCase();
    const email = (p.email || '').toLowerCase();
    const username = (p.username || '').toLowerCase();
    return name.includes(term) || email.includes(term) || username.includes(term);
  });

  const missingHire = canEditHire ? peopleMissingHireDate(people) : [];

  // --- Create/Edit Dialog ---
  const handleOpenDialog = (person = null) => {
    setDialogError('');
    if (person) {
      setEditMode(true);
      setCurrentPerson(person);
      setFormData({
        first_name: person.first_name,
        last_name: person.last_name,
        email: person.email,
        hourly_rate: person.hourly_rate ?? '',
        is_active: person.is_active,
        overtime_enabled: person.overtime_enabled !== false,
        overtime_threshold: person.overtime_threshold || 40,
        // A masked row (plan 102) has no overtime_multiplier key: show nothing rather than a fabricated 1.5.
        overtime_multiplier: person.overtime_multiplier === undefined ? '' : (person.overtime_multiplier || 1.5),
        overtime_exception: person.overtime_exception || '',
        overtime_exception_reason: person.overtime_exception_reason || '',
        overtime_disabled_basis: '',
        work_state: person.work_state || '',
        default_department_id: person.default_department_id || '',
        holiday_schedule_id: person.holiday_schedule_id || '',
        hire_date: person.hire_date ? person.hire_date.split('T')[0] : '',
        employee_type: person.employee_type || 'hourly',
        salary_amount: person.salary_amount ?? '',
        pay_frequency: person.pay_frequency || 'biweekly',
        filing_status: person.filing_status || 'single',
        federal_allowances: person.federal_allowances || 0,
        state_code: person.state_code || '',
        state_allowances: person.state_allowances || 0,
        additional_federal_withholding: person.additional_federal_withholding || 0,
        additional_state_withholding: person.additional_state_withholding || 0,
        ssn_last_four: person.ssn_last_four || '',
        date_of_birth: person.date_of_birth ? person.date_of_birth.split('T')[0] : '',
        w4_multiple_jobs: person.w4_multiple_jobs || false,
        w4_dependent_credits: person.w4_dependent_credits || 0,
        w4_other_income: person.w4_other_income || 0,
        w4_deductions: person.w4_deductions || 0,
        w4_extra_withholding: person.w4_extra_withholding || 0,
        w4_signed_date: person.w4_signed_date ? person.w4_signed_date.split('T')[0] : '',
        w4_basis: '',
        w4_lock_in_letter_date: person.w4_lock_in_letter_date ? person.w4_lock_in_letter_date.split('T')[0] : '',
        w4_received_date: person.w4_received_date ? person.w4_received_date.split('T')[0] : '',
        w4_lock_in_release_date: person.w4_lock_in_release_date ? person.w4_lock_in_release_date.split('T')[0] : '',
        local_eit_rate: person.local_eit_rate || '',
        local_lst_annual: person.local_lst_annual || '',
        local_tax_jurisdiction: person.local_tax_jurisdiction || '',
        pa_uc_employee_rate: person.pa_uc_employee_rate || '',
        resident_psd_code: person.resident_psd_code || '',
        work_psd_code: person.work_psd_code || '',
        local_tax_collector: person.local_tax_collector || '',
        street_address: person.street_address || '',
        city: person.city || '',
        zip_code: person.zip_code || '',
        pto_accrual_rate: person.pto_accrual_rate || 0,
        pto_accrual_frequency: person.pto_accrual_frequency || 'per_pay_period',
        pto_max_balance: person.pto_max_balance || 0,
        pto_carryover_limit: person.pto_carryover_limit || 0,
        pto_balance: person.pto_balance || 0,
        // Login
        loginEnabled: !!person.user_id,
        username: person.username || '',
        user_role: person.user_role_slug || person.user_role || 'user',
        user_id: person.user_id || null,
        user_is_active: person.user_is_active !== false,
        addingLogin: false,
        password: '',
        tenant_id: person.tenant_id || '',
      });
    } else {
      setEditMode(false);
      setCurrentPerson(null);
      setFormData({ ...defaultFormData, tenant_id: currentUser?.tenantId || '' });
      setUsernameManuallyEdited(false);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setDialogError('');
  };

  const generateUsername = (firstName, lastName) => {
    if (!firstName || !lastName) return '';
    return (firstName.charAt(0) + lastName).toLowerCase().replace(/[^a-z0-9]/g, '');
  };

  const [usernameManuallyEdited, setUsernameManuallyEdited] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const updated = { ...prev, [name]: value };
      const storedDay = (k) => (currentPerson?.[k] ? String(currentPerson[k]).split('T')[0] : '');
      if (name === 'w4_basis') {
        // A new employee W-4 never changes the IRS letter date: put the stored one back. Its receipt date
        // defaults to today in the company's zone (plan 107); the admin corrects it if the form arrived earlier.
        if (value === 'employee_w4') {
          updated.w4_lock_in_letter_date = storedDay('w4_lock_in_letter_date');
          updated.w4_received_date = todayInTimezone(getTenantTimezone());
        }
        // IRS letters and notices never carry the employee's receipt date; a non-release basis never edits the release date.
        if (value === 'irs_lock_in' || value === 'irs_lock_in_release') updated.w4_received_date = storedDay('w4_received_date');
        if (value !== 'irs_lock_in_release') updated.w4_lock_in_release_date = storedDay('w4_lock_in_release_date');
      }
      // Auto-suggest username: use email if available, fall back to name-based
      if (!editMode && !usernameManuallyEdited) {
        if (name === 'email' && value.includes('@')) {
          updated.username = value.toLowerCase().trim();
        } else if ((name === 'first_name' || name === 'last_name') && !updated.email) {
          updated.username = generateUsername(updated.first_name, updated.last_name);
        }
      }
      if (name === 'username') {
        setUsernameManuallyEdited(value !== '');
      }
      return updated;
    });
  };

  const renderRate = (person) => {
    if (person.pay_hidden === true) {
      return (
        <span>
          —
          <Tooltip title={PAY_SCOPE_HIDDEN_TEXT}>
            <InfoOutlined fontSize="inherit" color="action" sx={{ ml: 0.25, verticalAlign: 'middle' }} />
          </Tooltip>
        </span>
      );
    }
    if (person.hourly_rate === undefined && person.salary_amount === undefined) {
      return <Tooltip title="Hidden — needs the View pay rates permission"><span>—</span></Tooltip>;
    }
    return person.employee_type === 'salary'
      ? `$${parseFloat(person.salary_amount || 0).toLocaleString()}/yr`
      : `$${parseFloat(person.hourly_rate || 0).toFixed(2)}/hr`;
  };

  // Pay fields absent from the row = caller lacks View pay rates
  const payHidden = !!(editMode && currentPerson && currentPerson.hourly_rate === undefined);
  const payHiddenText = editMode && currentPerson?.pay_hidden === true ? PAY_SCOPE_HIDDEN_TEXT : 'Hidden — needs the View pay rates permission';
  // DOB / address / W-4 absent from the row = caller lacks Manage employees and
  // Run payroll (server shapeEmployeeRows). Edit then hides those sections.
  const piiHidden = !!(editMode && currentPerson
    && currentPerson.date_of_birth === undefined && currentPerson.filing_status === undefined);
  // SSN comes back as '****' without Manage users: never send it back, and lock the field.
  const ssnHidden = !!(editMode && currentPerson && currentPerson.ssn_last_four === '****');
  // OT Multiplier is a pay field. POST /employees applies it for overtime editors who can view
  // pay; PUT /employees/:id writes it only for the built-in admin / program admin (plan 088).
  const canEditMultiplier = canEditOvertime && hasPermission('can_view_pay_rates')
    && (!editMode || ['admin', 'program_admin'].includes(currentUser?.role));

  const handleSubmit = async () => {
    try {
      setDialogError('');

      if (editMode) {
        // Update employee fields
        const empData = { ...formData };
        delete empData.loginEnabled;
        delete empData.username;
        delete empData.user_role;
        delete empData.user_id;
        delete empData.user_is_active;
        delete empData.addingLogin;
        delete empData.password;
        delete empData.tenant_id;
        // Pay is hidden from this caller: never send the placeholder values back
        if (payHidden) {
          delete empData.hourly_rate;
          delete empData.salary_amount;
        }
        // PII is hidden from this caller: never send the form's placeholder values back
        if (piiHidden) {
          ['date_of_birth', 'state_code', 'street_address', 'city', 'zip_code', 'filing_status',
            'federal_allowances', 'state_allowances', 'additional_federal_withholding',
            'additional_state_withholding', 'w4_multiple_jobs', 'w4_dependent_credits', 'w4_other_income',
            'w4_deductions', 'w4_extra_withholding', 'w4_signed_date', 'local_eit_rate', 'local_lst_annual',
            'local_tax_jurisdiction', 'pa_uc_employee_rate', 'w4_basis', 'w4_lock_in_letter_date', 'w4_received_date', 'w4_lock_in_release_date', 'resident_psd_code', 'work_psd_code', 'local_tax_collector'].forEach(k => { delete empData[k]; });
        }
        // Overtime is locked for this caller: never send the form's copies back
        if (!canEditOvertime) {
          ['overtime_enabled', 'overtime_threshold', 'overtime_exception', 'overtime_exception_reason',
            'overtime_disabled_basis', 'work_state'].forEach(k => { delete empData[k]; });
        }
        if (!canEditMultiplier || payHidden) delete empData.overtime_multiplier;
        if (ssnHidden) delete empData.ssn_last_four;
        // A payroll admin's save carries only the section the server lets them change.
        if (taxOnlyEditor) {
          const keep = new Set(['date_of_birth', 'state_code', 'street_address', 'city', 'zip_code', 'filing_status',
            'federal_allowances', 'state_allowances', 'additional_federal_withholding',
            'additional_state_withholding', 'w4_multiple_jobs', 'w4_dependent_credits', 'w4_other_income',
            'w4_deductions', 'w4_extra_withholding', 'w4_signed_date', 'local_eit_rate', 'local_lst_annual',
            'local_tax_jurisdiction', 'pa_uc_employee_rate', 'w4_basis', 'w4_lock_in_letter_date', 'w4_received_date', 'w4_lock_in_release_date', 'resident_psd_code', 'work_psd_code', 'local_tax_collector']);
          Object.keys(empData).forEach(k => { if (!keep.has(k)) delete empData[k]; });
        }
        // IRS lock-in: the signed date is the employee's own; never send an edited copy.
        if (empData.w4_basis === 'irs_lock_in' || empData.w4_basis === 'irs_lock_in_release') { delete empData.w4_signed_date; delete empData.w4_received_date; }

        const empPromise = updateEmployee(currentPerson.id, empData);

        // Update user fields if user account exists
        const promises = [empPromise];
        if (canEditLogin && currentPerson.user_id && formData.loginEnabled) {
          const userData = {
            role: formData.user_role,
            isActive: formData.user_is_active,
          };
          promises.push(updateUser(currentPerson.user_id, userData));
        }

        // Adding login to existing employee-only person
        if (canEditLogin && !currentPerson.user_id && formData.loginEnabled && formData.addingLogin) {
          if (!formData.username) {
            setDialogError('Username is required');
            return;
          }
          if (!formData.password || !isPasswordValid(formData.password)) {
            const missing = getPasswordErrors(formData.password);
            setDialogError('Password requirements not met: ' + missing.join(', '));
            return;
          }
          promises.push(createUser({
            username: formData.username,
            password: formData.password,
            role: formData.user_role,
            employeeId: currentPerson.id,
            tenant_id: currentPerson.tenant_id,
          }));
        }

        await Promise.all(promises);
        handleCloseDialog();
        showSuccess('Person updated successfully');
      } else {
        // Create new person
        if (!formData.hire_date) {
          setDialogError('Hire Date is required');
          return;
        }
        const createData = {
          first_name: formData.first_name,
          last_name: formData.last_name,
          email: formData.email,
          hire_date: formData.hire_date,
          hourly_rate: formData.hourly_rate,
          // hourly | salary | contractor — the server defaults to hourly when omitted
          employee_type: formData.employee_type || 'hourly',
          overtime_enabled: formData.overtime_enabled,
          overtime_threshold: formData.overtime_threshold,
          overtime_exception: formData.overtime_exception,
          overtime_exception_reason: formData.overtime_exception_reason,
          overtime_disabled_basis: formData.overtime_disabled_basis,
          overtime_multiplier: formData.overtime_multiplier,
          work_state: formData.work_state || null,
          default_department_id: formData.default_department_id || null,
        };
        // Always include username — every person gets a login
        if (formData.username) {
          createData.username = formData.username;
        }
        if (isProgramAdmin && formData.tenant_id) {
          createData.tenant_id = formData.tenant_id;
        }

        const result = await createEmployee(createData);
        handleCloseDialog();

        if (result.data.temporaryPassword) {
          setTempPasswordDialog({
            open: true,
            password: result.data.temporaryPassword,
            employeeName: `${formData.first_name} ${formData.last_name}`,
          });
        }
        showSuccess('Person created successfully');
      }
      loadPeople();
    } catch (err) {
      const data = err.response?.data;
      if (data?.details?.length) {
        setDialogError(data.details.map(d => d.message).join('. '));
      } else {
        setDialogError(data?.error || 'Failed to save');
      }
      console.error(err);
    }
  };

  // --- Lockout / Reactivate ---
  const handleLockout = async (person) => {
    const action = person.is_active ? 'deactivate' : 'reactivate';
    const reason = person.is_active ? window.prompt(`Reason for deactivating ${person.first_name}?`, '') : null;
    if (person.is_active && reason === null) return;

    try {
      const res = await lockoutEmployee(person.id, !person.is_active, reason);
      showSuccess(`${person.first_name} ${person.last_name} ${action}d successfully.${finalAccrualText(res.data?.pto_final_accrual)}`);
      loadPeople();
    } catch (err) {
      setError(err.response?.data?.error || `Failed to ${action}`);
    }
  };

  // --- Delete ---
  const handleDelete = (person) => {
    setDeleteConfirmDialog({ open: true, employee: person, exported: false });
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteEmployee(deleteConfirmDialog.employee.id, true);
      setDeleteConfirmDialog({ open: false, employee: null, exported: false });
      showSuccess('Person deleted successfully');
      loadPeople();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete');
    }
  };

  const handleExportBeforeDelete = () => {
    const emp = deleteConfirmDialog.employee;
    exportToExcel({ employee_id: emp.id });
    setDeleteConfirmDialog(prev => ({ ...prev, exported: true }));
  };

  // --- W-4 ---
  const handleDownloadW4 = async (person) => {
    try {
      const res = await downloadW4PDF(person.id);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `W4_${person.first_name}_${person.last_name}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to download W-4 PDF');
    }
  };

  // --- Reset Password ---
  const handleOpenResetPassword = (person) => {
    setResetDialog({ open: true, user: { id: person.user_id, username: person.username } });
    setResetPassword('');
    setShowResetPassword(false);
  };

  const handleResetPasswordSubmit = async () => {
    if (!resetPassword || !isPasswordValid(resetPassword)) {
      setError('Password does not meet requirements');
      return;
    }
    try {
      await resetUserPassword(resetDialog.user.id, resetPassword);
      setResetDialog({ open: false, user: null });
      showSuccess('Password reset successfully');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reset password');
    }
  };

  const handleSendPasswordResetEmail = async (person) => {
    try {
      const response = await sendPasswordReset(person.user_id);
      showSuccess(`Password reset email sent! Temp password: ${response.data.tempPassword}`);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send password reset email');
    }
  };

  // --- Unlock ---
  const handleUnlockUser = async (person) => {
    const confirmed = await showConfirm({
      title: 'Unlock Account',
      message: `Unlock the account for "${person.username}"?`,
      confirmText: 'Unlock',
      confirmColor: 'warning',
    });
    if (!confirmed) return;
    try {
      await unlockUser(person.user_id);
      showSuccess(`Account for ${person.username} unlocked`);
      loadPeople();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to unlock');
    }
  };

  // --- Managed Employees ---
  const handleOpenManagedEmployees = async (person) => {
    const builtinRoles = ['user', 'manager', 'payroll_admin', 'admin', 'program_admin'];
    // Per-user switches only mean something for a manager with no role_id;
    // everyone else gets the permissions of their role (Settings > Roles).
    const legacyPerms = usesLegacyManagerPermissions(person);
    // allSettled so one failing call doesn't blank out the others
    const [managedRes, employeesRes, permsRes, registryRes] = await Promise.allSettled([
      getManagedEmployees(person.user_id),
      getEmployees(),
      legacyPerms ? getManagerPermissions(person.user_id) : Promise.resolve(null),
      getPermissionRegistry(),
    ]);
    setManagedEmployeeIds(managedRes.status === 'fulfilled' ? managedRes.value.data.map(e => e.employee_id) : []);
    setAllEmployees(employeesRes.status === 'fulfilled' ? employeesRes.value.data : []);
    setManagerPerms(permsRes.status === 'fulfilled' && permsRes.value ? permsRes.value.data.permissions || null : null);
    setPermRegistry(registryRes.status === 'fulfilled' && registryRes.value ? registryRes.value.data || {} : {});
    setManagedDialog({
      open: true,
      user: {
        id: person.user_id,
        username: person.username,
        role: person.user_role,
        isCustomRole: !!person.user_role_slug && !builtinRoles.includes(person.user_role_slug),
        roleBacked: !!person.user_role_id,
        legacyPerms,
        roleName: person.role_name,
        directReportPerms: person.direct_report_permissions || [],
      },
    });
  };

  const handleSaveManagedEmployees = async () => {
    try {
      const promises = [setManagedEmployees(managedDialog.user.id, managedEmployeeIds)];
      // Save permissions if this is a manager
      if (managedDialog.user?.legacyPerms && managerPerms) {
        promises.push(setManagerPermissions(managedDialog.user.id, managerPerms));
      }
      await Promise.all(promises);
      setManagedDialog({ open: false, user: null });
      setManagerPerms(null);
      showSuccess('Managed employees updated');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update managed employees');
    }
  };

  // --- Helpers ---
  const getRoleColor = (role) => {
    switch (role) {
      case 'admin': return 'error';
      case 'program_admin': return 'secondary';
      case 'payroll_admin': return 'info';
      case 'manager': return 'warning';
      default: return 'primary';
    }
  };

  const getRoleDisplayName = (slug, roleName) => {
    // Use the role_name from the DB join if available
    if (roleName) return roleName;
    if (!slug) return '';
    const role = roles.find(r => r.slug === slug);
    if (role) return role.name;
    // Fallback for built-in roles if roles haven't loaded
    const builtins = { user: 'Employee', manager: 'Manager', payroll_admin: 'Payroll Admin', admin: 'Admin', program_admin: 'Program Admin' };
    return builtins[slug] || slug;
  };

  // When editing, handle enabling login on an employee-only person
  const handleToggleLogin = (person) => {
    handleOpenDialog(person);
    // After state updates, set addingLogin
    setTimeout(() => {
      setFormData(prev => ({
        ...prev,
        loginEnabled: true,
        addingLogin: true,
        username: '',
        password: '',
        user_role: 'user',
      }));
    }, 0);
  };

  // Not admin/manager — show nothing (they don't see this tab)
  if (!canManage) return null;

  // --- Mobile Card View ---
  const renderMobileView = () => (
    <Stack spacing={1.5}>
      {filteredPeople.map(person => (
        <Card key={person.id} variant="outlined">
          <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography variant="subtitle1" fontWeight="medium">
                  {person.first_name} {person.last_name}
                </Typography>
                <Typography variant="body2" color="text.secondary" noWrap>
                  {person.email}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                <Chip
                  label={person.is_active ? 'Active' : 'Inactive'}
                  color={person.is_active ? 'success' : 'default'}
                  size="small"
                />
                {person.user_role && (
                  <Chip label={getRoleDisplayName(person.user_role, person.role_name)} color={getRoleColor(person.user_role_slug || person.user_role)} size="small" />
                )}
              </Box>
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
              {person.username ? (
                <Chip label={person.username} size="small" variant="outlined" />
              ) : (
                <Chip label="No Login" size="small" variant="outlined" color="default" />
              )}
              {(isAdmin || isProgramAdmin) && (
                <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: 'action.hover', borderRadius: 1, px: 1, py: 0.5 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>Rate:</Typography>
                  <Typography variant="caption" fontWeight="medium">
                    {renderRate(person)}
                  </Typography>
                </Box>
              )}
              {person.user_id && person.locked_until && new Date(person.locked_until) > new Date() && (
                <Chip label="Locked" color="error" size="small" />
              )}
            </Box>

            <Box sx={{ display: 'flex', gap: 1, borderTop: '1px solid', borderColor: 'divider', pt: 1.5, mt: 1, flexWrap: 'wrap' }}>
              <Button size="small" startIcon={<Edit />} onClick={() => handleOpenDialog(person)}>
                Edit
              </Button>
              <Button size="small"
                color={person.is_active ? 'warning' : 'success'}
                startIcon={person.is_active ? <Lock /> : <LockOpen />}
                onClick={() => handleLockout(person)}
              >
                {person.is_active ? 'Deactivate' : 'Reactivate'}
              </Button>
              {person.user_id && (
                <Button size="small" startIcon={<ResetPasswordIcon />} onClick={() => handleOpenResetPassword(person)}>
                  Reset
                </Button>
              )}
              {showDirectReports(person) && (
                <Button size="small" color="warning" startIcon={<PeopleIcon />} onClick={() => handleOpenManagedEmployees(person)}>
                  Employees
                </Button>
              )}
              {!person.user_id && (
                <Button size="small" color="info" onClick={() => handleToggleLogin(person)}>
                  Add Login
                </Button>
              )}
              {person.user_id && person.locked_until && new Date(person.locked_until) > new Date() && (
                <Button size="small" color="warning" startIcon={<LockOpen />} onClick={() => handleUnlockUser(person)}>
                  Unlock
                </Button>
              )}
              {canDownloadW4 && (
                <Button size="small" startIcon={<Description />} onClick={() => handleDownloadW4(person)}>
                  W-4
                </Button>
              )}
              {canSendPayRecords && (
                <Button size="small" startIcon={<ForwardToInbox />} onClick={() => setPayRecordsDialog({ open: true, person })}>
                  Pay records
                </Button>
              )}
              {(isAdmin || isProgramAdmin) && (
                <Button size="small" color="error" startIcon={<Delete />} onClick={() => handleDelete(person)}>
                  Delete
                </Button>
              )}
            </Box>
          </CardContent>
        </Card>
      ))}
      {filteredPeople.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography color="text.secondary">No people found</Typography>
        </Box>
      )}
    </Stack>
  );

  // --- Desktop Table View ---
  const renderDesktopView = () => (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Email</TableCell>
            <TableCell>Login</TableCell>
            <TableCell>Role</TableCell>
            {(isAdmin || isProgramAdmin) && (
              <TableCell>Pay Rate</TableCell>
            )}
            <TableCell>Status</TableCell>
            <TableCell>Last Login</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredPeople.map(person => (
            <TableRow key={person.id} hover>
              <TableCell>{person.first_name} {person.last_name}</TableCell>
              <TableCell>{person.email}</TableCell>
              <TableCell>
                {person.username || (
                  <Typography variant="body2" color="text.secondary">No login</Typography>
                )}
              </TableCell>
              <TableCell>
                {person.user_role ? (
                  <Chip label={getRoleDisplayName(person.user_role, person.role_name)} color={getRoleColor(person.user_role_slug || person.user_role)} size="small" />
                ) : (
                  <Typography variant="body2" color="text.secondary">—</Typography>
                )}
              </TableCell>
              {(isAdmin || isProgramAdmin) && (
                <TableCell>
                  {renderRate(person)}
                </TableCell>
              )}
              <TableCell>
                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                  <Chip
                    label={person.is_active ? 'Active' : 'Inactive'}
                    color={person.is_active ? 'success' : 'default'}
                    size="small"
                  />
                  {person.user_id && person.locked_until && new Date(person.locked_until) > new Date() && (
                    <Chip label="Locked" color="error" size="small" />
                  )}
                </Box>
              </TableCell>
              <TableCell>
                {person.last_login
                  ? new Date(person.last_login).toLocaleDateString()
                  : person.user_id ? 'Never' : '—'}
              </TableCell>
              <TableCell>
                <Tooltip title="Edit">
                  <IconButton size="small" onClick={() => handleOpenDialog(person)}>
                    <Edit />
                  </IconButton>
                </Tooltip>
                <Tooltip title={person.is_active ? 'Deactivate' : 'Reactivate'}>
                  <IconButton size="small"
                    color={person.is_active ? 'warning' : 'success'}
                    onClick={() => handleLockout(person)}
                  >
                    {person.is_active ? <Lock /> : <LockOpen />}
                  </IconButton>
                </Tooltip>
                {person.user_id && (
                  <Tooltip title="Reset Password">
                    <IconButton size="small" onClick={() => handleOpenResetPassword(person)}>
                      <ResetPasswordIcon />
                    </IconButton>
                  </Tooltip>
                )}
                {person.user_id && (
                  <Tooltip title="Send Password Reset Email">
                    <IconButton size="small" color="secondary" onClick={() => handleSendPasswordResetEmail(person)}>
                      <EmailIcon />
                    </IconButton>
                  </Tooltip>
                )}
                {showDirectReports(person) && (
                  <Tooltip title="Manage Employees">
                    <IconButton size="small" color="warning" onClick={() => handleOpenManagedEmployees(person)}>
                      <PeopleIcon />
                    </IconButton>
                  </Tooltip>
                )}
                {!person.user_id && (
                  <Tooltip title="Add Login Access">
                    <IconButton size="small" color="info" onClick={() => handleToggleLogin(person)}>
                      <LockOpen />
                    </IconButton>
                  </Tooltip>
                )}
                {person.user_id && person.locked_until && new Date(person.locked_until) > new Date() && (
                  <Tooltip title="Unlock Account">
                    <IconButton size="small" color="warning" onClick={() => handleUnlockUser(person)}>
                      <LockOpen />
                    </IconButton>
                  </Tooltip>
                )}
                {canDownloadW4 && (
                  <Tooltip title="Download W-4 PDF">
                    <IconButton size="small" onClick={() => handleDownloadW4(person)}>
                      <Description />
                    </IconButton>
                  </Tooltip>
                )}
                {canSendPayRecords && (
                  <Tooltip title="Send pay records">
                    <IconButton size="small" onClick={() => setPayRecordsDialog({ open: true, person })}>
                      <ForwardToInbox />
                    </IconButton>
                  </Tooltip>
                )}
                {(isAdmin || isProgramAdmin) && (
                  <Tooltip title="Delete">
                    <IconButton size="small" color="error" onClick={() => handleDelete(person)}>
                      <Delete />
                    </IconButton>
                  </Tooltip>
                )}
              </TableCell>
            </TableRow>
          ))}
          {filteredPeople.length === 0 && (
            <TableRow>
              <TableCell colSpan={8} align="center">
                <Typography color="text.secondary" sx={{ py: 2 }}>No people found</Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box sx={{ pb: isMobile ? 10 : 0 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight="bold">People</Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexGrow: isMobile ? 1 : 0 }}>
          <TextField
            size="small"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
            }}
            sx={{ minWidth: 200 }}
          />
          {!isMobile && (
            <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenDialog()}>
              Add Person
            </Button>
          )}
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
      {missingHire.length > 0 && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={searchTerm ? (
            <Tooltip title="Clear the search box so everyone is listed">
              <Button color="inherit" size="small" onClick={() => setSearchTerm('')}>Show everyone</Button>
            </Tooltip>
          ) : null}
        >
          <AlertTitle>
            {missingHire.length === 1 ? '1 person has no Hire Date.' : `${missingHire.length} people have no Hire Date.`}
          </AlertTitle>
          PTO accrual, FMLA eligibility and I-9 retention are counted from it. Click Edit on each person and fill in Hire Date.
          {' '}
          {missingHire.slice(0, 5).map((m) => `${m.first_name} ${m.last_name}${m.tenant_name ? ` (${m.tenant_name})` : ''}`).join(', ')}
          {missingHire.length > 5 ? ` and ${missingHire.length - 5} more` : ''}
        </Alert>
      )}

      {isMobile ? renderMobileView() : renderDesktopView()}

      {/* Mobile FAB */}
      {isMobile && (
        <Fab color="primary" aria-label="add person"
          onClick={() => handleOpenDialog()}
          sx={{ position: 'fixed', bottom: 80, right: 16, zIndex: 1000 }}
        >
          <Add />
        </Fab>
      )}

      {/* Create/Edit Dialog */}
      <PersonFormDialog
        open={openDialog}
        onClose={handleCloseDialog}
        onSubmit={handleSubmit}
        error={dialogError}
        editMode={editMode}
        payHidden={payHidden}
        payHiddenText={payHiddenText}
        piiHidden={piiHidden}
        canEditTax={canEditTax}
        taxOnly={taxOnlyEditor}
        w4Original={editMode ? currentPerson : null}
        canEditLogin={canEditLogin}
        ssnHidden={ssnHidden}
        formData={formData}
        setFormData={setFormData}
        handleInputChange={handleInputChange}
        isAdmin={isAdmin}
        isProgramAdmin={isProgramAdmin}
        canEditOvertime={canEditOvertime}
        canEditMultiplier={canEditMultiplier}
        departmentsEnabled={departmentsEnabled}
        departments={departments}
        holidaySchedules={holidaySchedules}
        tenants={tenants}
        roles={roles}
        currentUser={currentUser}
        generateUsername={generateUsername}
      />

      {/* Delete Dialog */}
      <DeleteEmployeeDialog
        open={deleteConfirmDialog.open}
        onClose={() => setDeleteConfirmDialog({ open: false, employee: null, exported: false })}
        employee={deleteConfirmDialog.employee}
        exported={deleteConfirmDialog.exported}
        onExport={handleExportBeforeDelete}
        onConfirmDelete={handleConfirmDelete}
      />

      {/* Temp Password Dialog */}
      <TempPasswordDialog
        open={tempPasswordDialog.open}
        onClose={() => setTempPasswordDialog({ open: false, password: '', employeeName: '' })}
        employeeName={tempPasswordDialog.employeeName}
        password={tempPasswordDialog.password}
      />

      <SendPayRecordsDialog
        open={payRecordsDialog.open}
        person={payRecordsDialog.person}
        onClose={() => setPayRecordsDialog({ open: false, person: null })}
        onSent={(msg) => showSuccess(msg)}
      />

      {/* Reset Password Dialog */}
      <Dialog open={resetDialog.open} onClose={() => setResetDialog({ open: false, user: null })} maxWidth="sm" fullWidth>
        <DialogTitle>Reset Password for {resetDialog.user?.username}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="New Password"
            type={showResetPassword ? 'text' : 'password'}
            value={resetPassword}
            onChange={(e) => setResetPassword(e.target.value)}
            margin="normal"
            required
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowResetPassword(!showResetPassword)} edge="end">
                    {showResetPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          {resetPassword && <PasswordStrengthIndicator password={resetPassword} compact />}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetDialog({ open: false, user: null })}>Cancel</Button>
          <Button variant="contained" onClick={handleResetPasswordSubmit}>Reset Password</Button>
        </DialogActions>
      </Dialog>

      {/* Managed Employees Dialog */}
      <Dialog open={managedDialog.open} onClose={() => { setManagedDialog({ open: false, user: null }); setManagerPerms(null); }} maxWidth="sm" fullWidth>
        <DialogTitle>Manage Employees for {managedDialog.user?.username}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FormControl fullWidth margin="normal" sx={{ flex: 1 }}>
            <InputLabel>Employees to Manage</InputLabel>
            <Select
              multiple
              value={managedEmployeeIds}
              onChange={(e) => setManagedEmployeeIds(e.target.value)}
              input={<OutlinedInput label="Employees to Manage" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((id) => {
                    const emp = allEmployees.find(e => e.id === id);
                    return (
                      <Chip key={id} label={emp ? `${emp.first_name} ${emp.last_name}` : id} size="small" />
                    );
                  })}
                </Box>
              )}
            >
              {allEmployees.map((emp) => (
                <MenuItem key={emp.id} value={emp.id}>
                  <Checkbox checked={managedEmployeeIds.indexOf(emp.id) > -1} />
                  <ListItemText primary={`${emp.first_name} ${emp.last_name}`} secondary={emp.email} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
            <Tooltip title={directReportsTip(managedDialog.user, permRegistry)}>
              <InfoOutlined fontSize="small" color="action" sx={{ mt: 1 }} />
            </Tooltip>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            {directReportsCaption(managedDialog.user, permRegistry)}
          </Typography>

          {/* Custom role info */}
          {(managedDialog.user?.isCustomRole || (managedDialog.user?.role === 'manager' && managedDialog.user?.roleBacked)) && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Permissions</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                This user has the "{managedDialog.user.roleName || managedDialog.user.role}" role. Permissions come from that role on Settings {'>'} Roles, not per user. To give one manager a different set, create a custom role there and pick it in the Role field when you edit this person.
              </Typography>
            </>
          )}

          {/* Legacy manager permissions — only for built-in manager role */}
          {managedDialog.user?.legacyPerms && managerPerms && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Permissions</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                Control what this manager can access. Changes require re-login.
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                {legacyPermissionToggles(managerPerms, permRegistry).map(({ key, label, description }) => (
                  <Box key={key} sx={{ display: 'flex', alignItems: 'center' }}>
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={managerPerms[key] === true}
                          onChange={(e) => setManagerPerms(prev => ({ ...prev, [key]: e.target.checked }))}
                        />
                      }
                      label={<Typography variant="body2">{label}</Typography>}
                    />
                    {description && (
                      <Tooltip title={description}>
                        <InfoOutlined fontSize="small" color="action" sx={{ ml: -1 }} />
                      </Tooltip>
                    )}
                  </Box>
                ))}
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setManagedDialog({ open: false, user: null }); setManagerPerms(null); }}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveManagedEmployees}>Save</Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialogComponent />
    </Box>
  );
}
